import { useScopedTranslation } from 'application/localization/useScopedTranslation.ts';
import { useAdvancedForm } from 'helpers/form/useAdvancedForm.ts';
import { requiredRule } from 'helpers/form/react-hook-form-helper.ts';
import { useWatch } from 'react-hook-form';
import styles from './MainboardReplacerForm.module.scss';
import clsx from 'clsx';
import { MainboardCertificateClient } from 'services/api/api-client.ts';
import { Input } from '../../../components/uikit/inputs/Input.tsx';
import { Button } from '../../../components/uikit/buttons/Button.tsx';
import { useState } from 'react';
import { MainboardReplacementResult } from './MainboardReplacementResult.tsx';
import { ReplacementResultProps } from './MainboardReplacementResult.tsx';
import { Breadcrumbs } from '../../../components/uikit/Breadcrumbs.tsx';
import { Autocomplete, TextField, useEventCallback } from '@mui/material';
import ArrowDown from 'assets/icons/arrow-down-selector.svg?react';
import { FileDropzone } from '../../../components/uikit/FileDropzone.tsx';
import { TextArea } from '../../../components/uikit/inputs/TextArea.tsx';
import { useAnalytics } from '../../../helpers/analytics.ts';
import { Link } from 'react-router-dom';
import { Links } from '../../../application/constants/links.ts';
import { ActionText } from '../../../components/uikit/ActionText.tsx';

type Form = {
  failureCategory: string;
  otherCategoryDetails: string;
  oldMbSerialNumber: string;
  requestFile: File | null;
};

const failureCategories: string[] = [
  'ERROR 103',
  'ERROR 108',
  'ERROR 119',
  'ERROR 202',
  'ERROR 203',
  'ERROR 622',
  'ERROR 628',
  'ERROR 630',
  'ERROR 816',
  'ERROR 826',
  'Boot/Startup failure',
  'Water damage',
  'Mechanical damage',
  'Network problem without error number',
  'Device does not power on',
  'Screen issue',
  'Send Fail',
  'Other',
];

export const MainboardReplacerCategory = 'Mainboard replacer';

export const MainboardReplacerForm = () => {
  const [resultProps, setResultProps] = useState<ReplacementResultProps | null>(
    null,
  );

  const i18n = useScopedTranslation('Page.MainboardReplacer');
  const { trackEvent } = useAnalytics();

  const trackMainboardEvent = useEventCallback(
    (action: string, name?: string) => {
      trackEvent({
        category: MainboardReplacerCategory,
        action,
        name,
      });
    },
  );

  const form = useAdvancedForm<Form>(
    async (data) => {
      const response = await MainboardCertificateClient.submit(
        data.oldMbSerialNumber,
        data.failureCategory,
        data.otherCategoryDetails,
        data.requestFile
          ? { data: data.requestFile, fileName: data.requestFile.name }
          : null,
      );

      trackMainboardEvent(
        'Submit mainboard replacer form',
        `Serial number: ${data.oldMbSerialNumber}, Category: ${
          data.failureCategory
        }, File: ${data.requestFile?.name ?? 'none'}`,
      );

      const tryAgain = () => {
        setResultProps(null);
        form.setValue('failureCategory', data.failureCategory, {
          shouldDirty: true,
        });
        form.setValue('otherCategoryDetails', data.otherCategoryDetails, {
          shouldDirty: true,
        });
        form.setValue('oldMbSerialNumber', data.oldMbSerialNumber, {
          shouldDirty: true,
        });
        form.setValue('requestFile', data.requestFile, { shouldDirty: true });
      };

      setResultProps({
        operationId: response.pkiReplacementOperationId!,
        operationCreatedAt: response.originalOperationCreatedAt,
        trackMainboardEvent,
        tryAgain: tryAgain,
        generateNew: () => {
          setResultProps(null);
          form.reset();
        },
      });
    },
    {
      defaultValues: {
        failureCategory: '',
        otherCategoryDetails: '',
        oldMbSerialNumber: '',
        requestFile: null,
      },
    },
  );

  const [
    failureCategory,
    otherCategoryDetails,
    oldMbSerialNumber,
    requestFile,
  ] = useWatch({
    control: form.control,
    name: [
      'failureCategory',
      'otherCategoryDetails',
      'oldMbSerialNumber',
      'requestFile',
    ],
  });

  const isOtherSelected = failureCategory === 'Other';

  const handleSubmit = async () => {
    const result = await form.trigger();
    if (!failureCategory) {
      form.setError('failureCategory', {
        type: 'required',
        message: i18n.t('required_error'),
      });
    }
    if (!requestFile) {
      form.setError('requestFile', {
        type: 'required',
        message: i18n.t('required_error'),
      });
    }
    if (isOtherSelected && !otherCategoryDetails) {
      form.setError('otherCategoryDetails', {
        type: 'required',
        message: i18n.t('required_error'),
      });
      return;
    }

    if (!result) return;

    void form.handleSubmitDefault();
  };

  const handleFileSelected = (file: File | null) => {
    form.clearErrors('requestFile');
    form.setOverallError('');
    form.setValue('requestFile', file, { shouldValidate: true });

    if (file) {
      trackMainboardEvent(
        'File selected',
        `File name: ${file.name}, Size: ${Math.round(file.size / 1024)} KB`,
      );
    }
  };

  return (
    <>
      <Breadcrumbs
        children={
          <Link
            to={Links.Authorized.CertificateInstruction.route}
            className={styles.textUnderline}
          >
            <ActionText text={i18n.t('instruction_link')} />
          </Link>
        }
      />
      <div
        className={styles.mainboardPage}
        data-test-id="mainboard-replacer-container"
      >
        <div className={styles.container}>
          {!resultProps?.operationId && (
            <div className={styles.contentLeft}>
              <div className={styles.h3}>{i18n.t('title')}</div>
              <div className={styles.greyText}>{i18n.t('description')}</div>
            </div>
          )}

          {!!resultProps?.operationId ? (
            <MainboardReplacementResult {...resultProps} />
          ) : (
            <>
              <div className={styles.containerItem}>
                <div className={clsx(styles.greyText)}>
                  {i18n.t('failure_category')}
                </div>

                <Autocomplete
                  className={clsx(styles.autoComplete)}
                  options={failureCategories}
                  getOptionLabel={(opt) => opt}
                  value={failureCategory}
                  onChange={(_, newVal) => {
                    form.clearErrors('failureCategory');
                    form.setValue('failureCategory', newVal!, {
                      shouldDirty: true,
                    });
                    trackMainboardEvent('Failure category selected', newVal!);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      inputProps={{
                        ...params.inputProps,
                        'data-test-id': 'mainboard-failure-category-input',
                      }}
                      error={!!form.formState.errors.failureCategory}
                      helperText={
                        form.formState.errors.failureCategory?.message
                      }
                      className={clsx(
                        styles.input,
                        !!form.formState.errors.failureCategory &&
                          styles.textFileErrorBorder,
                      )}
                      placeholder={i18n.t('failure_category_placeholder')}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} className={clsx(styles.selectItem)}>
                      <div className={styles.p1RegLine}>{option}</div>
                    </li>
                  )}
                  size="small"
                  popupIcon={
                    <ArrowDown onMouseDown={(e) => e.preventDefault()} />
                  }
                  disablePortal={true}
                  disableCloseOnSelect={false}
                />
              </div>
              {isOtherSelected && (
                <div className={styles.containerItem}>
                  <TextArea
                    testId={'mainboard-other-category-details-input'}
                    errorText={
                      form.formState.errors.otherCategoryDetails?.message
                    }
                    fullWidth={true}
                    placeholder={i18n.t('other_category_placeholder')}
                    value={otherCategoryDetails}
                    onChange={(e) => {
                      form.clearErrors('otherCategoryDetails');
                      form.setValue('otherCategoryDetails', e.target.value, {
                        shouldValidate: true,
                      });
                    }}
                  />
                  <div className={styles.hint}>
                    {i18n.t('other_details_hint')}
                  </div>
                </div>
              )}

              <div className={clsx(styles.containerItem, styles.marginTop20)}>
                <Input
                  {...form.register(
                    'oldMbSerialNumber',
                    requiredRule(i18n.t('required_error')),
                  )}
                  testId={'mainboard-old-serial-number-input'}
                  errorText={form.formState.errors.oldMbSerialNumber?.message}
                  onChange={(e) => {
                    form.clearErrors('oldMbSerialNumber');
                    form.setOverallError('');
                    form.setValue('oldMbSerialNumber', e.target.value.trim(), {
                      shouldValidate: true,
                    });
                  }}
                  className={clsx(styles.input)}
                  topHelperText={i18n.t('serial_number')}
                  placeholder={i18n.t('serial_number_placeholder')}
                  value={oldMbSerialNumber}
                  fullWidth
                />
              </div>
              <div className={styles.containerItem}>
                <div
                  className={styles.dropZone}
                  data-test-id="mainboard-request-file-container"
                >
                  <FileDropzone
                    onFileSelected={handleFileSelected}
                    supportedFileTypes={['.req']}
                    selectedFileProps={requestFile}
                    errorText={form.formState.errors.requestFile?.message}
                  />
                </div>
              </div>
              <div className={styles.footerContainer}>
                <div className={styles.error}>{form.overallError}</div>

                <Button
                  testId={'mainboard-submit-button'}
                  className={clsx(styles.submit)}
                  loading={form.formState.isSubmitting}
                  disabled={form.formState.isSubmitting}
                  title={i18n.t('submit')}
                  type={'button'}
                  onClick={handleSubmit}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};
